import { Component, Inject, OnDestroy, OnInit, ElementRef, ViewChild, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { DiscountFormData } from '../../../../shared/models/discount-form-data';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { AssignedCriteria, BusEntry, CriteriaEntry } from '../../../../shared/models/discount';
import { MatSelectModule } from '@angular/material/select';
import { AdminService } from '../../services/admin';
import { Partner } from '../../../../shared/models/partner';
import { Entity } from '../../../../shared/models/entity';
import { Route } from '../../../../shared/models/route';
import { Bus } from '../../../../shared/models/bus';
import { BehaviorSubject, Subject, Observable, combineLatest, of } from 'rxjs';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { startWith, map, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatChipsModule } from '@angular/material/chips';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';

export interface BusEntryFormGroup {
    id     : FormControl<string>;
    amount : FormControl<number | null>;
}

export interface CriteriaEntryFormGroup {
    id               : FormControl<string>;
    amount           : FormControl<number | null>;
    buses            : FormArray<FormGroup<BusEntryFormGroup>>;
    busSearchControl : FormControl<string | null>;
}

export interface MapEntryFormGroup {
    key                : FormControl<string>;
    values             : FormArray<FormGroup<CriteriaEntryFormGroup>>;
    valueSearchControl : FormControl<string | null>;
}

@Component({
    selector: 'app-discount-form',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule, MatButtonModule, MatIconModule, MatDialogTitle, MatDialogContent, MatFormFieldModule, MatInputModule, MatSelectModule, MatAutocompleteModule, MatExpansionModule, MatChipsModule, MatSlideToggleModule],
    templateUrl: './discount-form.html',
    styleUrl: './discount-form.css'
})
export class DiscountForm implements OnInit, OnDestroy {
    discountForm : FormGroup;
    isEditMode   : boolean = false;

    partners : Partner[] = [];
    entities : Entity[]  = [];
    routes   : Route[]   = [];
    buses    : Bus[]     = [];

    private _partnersSubject = new BehaviorSubject<Partner[]>([]);
    private _entitiesSubject = new BehaviorSubject<Entity[]>([]);
    private _routesSubject   = new BehaviorSubject<Route[]>([]);
    private _busesSubject    = new BehaviorSubject<Bus[]>([]);
    private destroy$         = new Subject<void>();
    private previousDefaultAmount: number | null = null;

    partnerSearchControl : FormControl<string | null> = new FormControl<string | null>(null);
    entitySearchControl  : FormControl<string | null> = new FormControl<string | null>(null);
    routeSearchControl   : FormControl<string | null> = new FormControl<string | null>(null);
    busSearchControl     : FormControl<string | null> = new FormControl<string | null>(null);

    filteredPartners$ : Observable<Partner[]> = of([]);
    filteredEntities$ : Observable<Entity[]>  = of([]);
    filteredRoutes$   : Observable<Route[]>   = of([]);
    filteredBuses$    : Observable<Bus[]>     = of([]);

    @ViewChild('partnerInputRef') partnerInputRef!: ElementRef<HTMLInputElement>;
    @ViewChild('entityInputRef')  entityInputRef!: ElementRef<HTMLInputElement>;
    @ViewChild('routeInputRef')   routeInputRef!: ElementRef<HTMLInputElement>;
    @ViewChild('busInputRef')     busInputRef!: ElementRef<HTMLInputElement>;

    mapPartnerKeySearchControl : FormControl<Partner | string | null> = new FormControl<Partner | string | null>(null);
    mapRouteKeySearchControl   : FormControl<Route | string | null>   = new FormControl<Route | string | null>(null);

    filteredMapPartnerKeys$ : Observable<Partner[]> = of([]);
    filteredMapRouteKeys$   : Observable<Route[]>   = of([]);

    @ViewChild('mapPartnerKeyInputRef') mapPartnerKeyInputRef!: ElementRef<HTMLInputElement>;
    @ViewChild('mapRouteKeyInputRef')   mapRouteKeyInputRef!: ElementRef<HTMLInputElement>;

    assignmentTypes = [
        { value: 'GLOBAL',         label: 'Global' },
        { value: 'PARTNER',        label: 'Por Cliente' },
        { value: 'ENTITY',         label: 'Por Entidad' },
        { value: 'ROUTE',          label: 'Por Ruta' },
        { value: 'BUS',            label: 'Por Bus' },
        { value: 'PARTNER_ENTITY', label: 'Por Cliente y Entidad' },
        { value: 'ROUTE_ENTITY',   label: 'Por Ruta y Entidad' },
        { value: 'PARTNER_ROUTE',  label: 'Por Cliente y Ruta' }
    ];

    constructor(
        private fb: FormBuilder,
        private adminService: AdminService,
        public dialogRef: MatDialogRef<DiscountForm, DiscountFormData | undefined>,
        @Inject(MAT_DIALOG_DATA) public data: DiscountFormData | null,
        private cdr: ChangeDetectorRef
    ) {
        this.isEditMode = !!data;

        this.discountForm = this.fb.group({
            name                : [this.data?.name || '', [Validators.required]],
            assignmentType      : [this.data?.assignmentType || 'GLOBAL', [Validators.required]],
            defaultAmount       : [this.data?.defaultAmount ?? null, [Validators.required, Validators.min(0)]],
            calculationStrategy : [this.data?.calculationStrategy || 'PER_UNIT_FLAT_PERIOD', [Validators.required]],
            status              : [this.data?.status ?? true]
        });

        this.previousDefaultAmount = this.data?.defaultAmount ?? null;

        if (this.data?.assignmentType && this.data.assignmentType !== 'GLOBAL') {
            this.discountForm.addControl('assignedCriteria', this.createAssignedCriteriaGroup());
        }
    }

    private createAssignedCriteriaGroup(): FormGroup {
        return this.fb.group({
            partnerIds       : this.fb.array<FormGroup<CriteriaEntryFormGroup>>([]),
            entityIds        : this.fb.array<FormGroup<CriteriaEntryFormGroup>>([]),
            routeIds         : this.fb.array<FormGroup<CriteriaEntryFormGroup>>([]),
            busIds           : this.fb.array<FormGroup<CriteriaEntryFormGroup>>([]),
            partnerEntityMap : this.fb.array<FormGroup<MapEntryFormGroup>>([]),
            routeEntityMap   : this.fb.array<FormGroup<MapEntryFormGroup>>([]),
            partnerRouteMap  : this.fb.array<FormGroup<MapEntryFormGroup>>([]),
        });
    }

    private ensureAssignedCriteriaPresence(): void {
        if (!this.assignedCriteriaControl) {
            this.discountForm.addControl('assignedCriteria', this.createAssignedCriteriaGroup());
        }
    }

    onCalculationStrategyToggle(checked: boolean): void {
        this.calculationStrategyControl?.setValue(checked ? 'PER_UNIT_PER_DAY' : 'PER_UNIT_FLAT_PERIOD');
    }

    ngOnInit(): void {
        this.loadRelatedData().then(() => {
            this._partnersSubject.next(this.partners);
            this._entitiesSubject.next(this.entities);
            this._routesSubject.next(this.routes);
            this._busesSubject.next(this.buses);
            this.cdr.detectChanges();
        });

        this.defaultAmountControl?.valueChanges.pipe(
            distinctUntilChanged(),
            takeUntil(this.destroy$)
        ).subscribe((newAmount) => {
            this.propagateDefaultAmountChange(this.previousDefaultAmount, newAmount);
            this.previousDefaultAmount = newAmount;
        });

        this.assignmentTypeControl?.valueChanges.pipe(
            distinctUntilChanged(),
            takeUntil(this.destroy$)
        ).subscribe((type: DiscountFormData['assignmentType']) => {
            this.ensureAssignedCriteriaPresence();

            if (type && this.assignedCriteriaControl) {
                this.updateAssignedCriteriaFormArrays(type);
            }

            this.cdr.detectChanges();
        });

        this.initFilters();
        this.ensureAssignedCriteriaPresence();

        if (this.isEditMode && this.data?.assignmentType && this.data.assignedCriteria) {
            this.updateAssignedCriteriaFormArrays(this.data.assignmentType, this.data.assignedCriteria);
        }
    }

    private initFilters(): void {
        this.filteredPartners$ = combineLatest([this.partnerSearchControl.valueChanges.pipe(startWith('')), this._partnersSubject.asObservable()]).pipe(
            map(([value, allItems]) => this._filterItems(value, allItems, this.partnerIdsFormArray?.value?.map((p: any) => p.id) || [], 'displayName', 'uid'))
        );

        this.filteredEntities$ = combineLatest([this.entitySearchControl.valueChanges.pipe(startWith('')), this._entitiesSubject.asObservable()]).pipe(
            map(([value, allItems]) => this._filterItems(value, allItems, this.entityIdsFormArray?.value?.map((e: any) => e.id) || [], 'name', 'id'))
        );

        this.filteredRoutes$ = combineLatest([this.routeSearchControl.valueChanges.pipe(startWith('')), this._routesSubject.asObservable()]).pipe(
            map(([value, allItems]) => this._filterItems(value, allItems, this.routeIdsFormArray?.value?.map((r: any) => r.id) || [], 'name', 'id'))
        );

        this.filteredBuses$ = combineLatest([this.busSearchControl.valueChanges.pipe(startWith('')), this._busesSubject.asObservable()]).pipe(
            map(([value, allItems]) => this._filterItems(value, allItems, this.busIdsFormArray?.value?.map((b: any) => b.id) || [], 'name', 'id'))
        );

        this.filteredMapPartnerKeys$ = combineLatest([this.mapPartnerKeySearchControl.valueChanges.pipe(startWith('')), this._partnersSubject.asObservable()]).pipe(
            map(([value, allItems]) => {
                const type         = this.assignmentTypeControl?.value;
                const activeMap    = type === 'PARTNER_ROUTE' ? this.partnerRouteMapGroupsControl : this.partnerEntityMapGroupsControl;
                const selectedKeys = activeMap?.value?.map((p: any) => p.key) || [];
                return this._filterItems(value, allItems, selectedKeys, 'displayName', 'uid');
            })
        );

        this.filteredMapRouteKeys$ = combineLatest([this.mapRouteKeySearchControl.valueChanges.pipe(startWith('')), this._routesSubject.asObservable()]).pipe(
            map(([value, allItems]) => this._filterItems(value, allItems, this.routeEntityMapGroupsControl?.value?.map((r: any) => r.key) || [], 'name', 'id'))
        );
    }

    // ─── Getters ────────────────────────────────────────────────────────────────

    get nameControl()                   : AbstractControl | null { return this.discountForm.get('name'); }
    get defaultAmountControl()          : AbstractControl | null { return this.discountForm.get('defaultAmount'); }
    get assignedCriteriaControl()       : FormGroup | null { return this.discountForm.get('assignedCriteria') as FormGroup; }
    get assignmentTypeControl()         : AbstractControl | null { return this.discountForm.get('assignmentType'); }
    get calculationStrategyControl()    : AbstractControl | null { return this.discountForm.get('calculationStrategy'); }
    get partnerIdsFormArray()           : FormArray<FormGroup<CriteriaEntryFormGroup>> { return this.assignedCriteriaControl?.get('partnerIds') as FormArray<FormGroup<CriteriaEntryFormGroup>>; }
    get entityIdsFormArray()            : FormArray<FormGroup<CriteriaEntryFormGroup>> { return this.assignedCriteriaControl?.get('entityIds') as FormArray<FormGroup<CriteriaEntryFormGroup>>; }
    get routeIdsFormArray()             : FormArray<FormGroup<CriteriaEntryFormGroup>> { return this.assignedCriteriaControl?.get('routeIds') as FormArray<FormGroup<CriteriaEntryFormGroup>>; }
    get busIdsFormArray()               : FormArray<FormGroup<CriteriaEntryFormGroup>> { return this.assignedCriteriaControl?.get('busIds') as FormArray<FormGroup<CriteriaEntryFormGroup>>; }
    get partnerEntityMapGroupsControl() : FormArray<FormGroup<MapEntryFormGroup>> { return this.assignedCriteriaControl?.get('partnerEntityMap') as FormArray<FormGroup<MapEntryFormGroup>>; }
    get routeEntityMapGroupsControl()   : FormArray<FormGroup<MapEntryFormGroup>> { return this.assignedCriteriaControl?.get('routeEntityMap') as FormArray<FormGroup<MapEntryFormGroup>>; }
    get partnerRouteMapGroupsControl()  : FormArray<FormGroup<MapEntryFormGroup>> { return this.assignedCriteriaControl?.get('partnerRouteMap') as FormArray<FormGroup<MapEntryFormGroup>>; }

    // ─── Default amount propagation ──────────────────────────────────────────────

    private propagateDefaultAmountChange(oldAmount: number | null, newAmount: number | null): void {
        this.updateCriteriaAmounts(this.partnerIdsFormArray, oldAmount, newAmount);
        this.updateCriteriaAmounts(this.entityIdsFormArray, oldAmount, newAmount);
        this.updateCriteriaAmounts(this.routeIdsFormArray, oldAmount, newAmount);
        this.updateCriteriaAmounts(this.busIdsFormArray, oldAmount, newAmount);
        this.updateMapAmounts(this.partnerEntityMapGroupsControl, oldAmount, newAmount);
        this.updateMapAmounts(this.routeEntityMapGroupsControl, oldAmount, newAmount);
        this.updateMapAmounts(this.partnerRouteMapGroupsControl, oldAmount, newAmount);
    }

    private updateCriteriaAmounts(array: FormArray<FormGroup<CriteriaEntryFormGroup>> | undefined, oldAmount: number | null, newAmount: number | null): void {
        if (!array) return;

        array.controls.forEach(group => {
            if (group.controls.amount.value === oldAmount) {
                group.controls.amount.setValue(newAmount, { emitEvent: false });
            }

            group.controls.buses.controls.forEach(busGroup => {
                if (busGroup.controls.amount.value === oldAmount) {
                    busGroup.controls.amount.setValue(newAmount, { emitEvent: false });
                }
            });
        });
    }

    private updateMapAmounts(array: FormArray<FormGroup<MapEntryFormGroup>> | undefined, oldAmount: number | null, newAmount: number | null): void {
        if (!array) return;

        array.controls.forEach(mapGroup => this.updateCriteriaAmounts(mapGroup.controls.values, oldAmount, newAmount));
    }

    // ─── Form array population ───────────────────────────────────────────────────

    private createCriteriaEntryFormGroup(entry: { id: string; amount?: number | null; buses?: BusEntry[] }): FormGroup<CriteriaEntryFormGroup> {
        const defaultAmt = this.defaultAmountControl?.value ?? null;
        const amount     = entry.amount !== undefined && entry.amount !== null ? entry.amount : defaultAmt;

        const busArray = this.fb.array<FormGroup<BusEntryFormGroup>>(
            (entry.buses || []).map(b => this.createBusEntryFormGroup(b, amount))
        );

        return this.fb.group<CriteriaEntryFormGroup>({
            id               : this.fb.control(entry.id, { nonNullable: true, validators: [Validators.required] }),
            amount           : this.fb.control(amount, { nonNullable: false, validators: [Validators.required, Validators.min(0)] }),
            buses            : busArray,
            busSearchControl : this.fb.control(null)
        });
    }

    private createBusEntryFormGroup(entry: { id: string; amount?: number | null }, parentAmount: number | null): FormGroup<BusEntryFormGroup> {
        const amount = entry.amount !== undefined && entry.amount !== null ? entry.amount : parentAmount;

        return this.fb.group<BusEntryFormGroup>({
            id     : this.fb.control(entry.id, { nonNullable: true, validators: [Validators.required] }),
            amount : this.fb.control(amount, { nonNullable: false, validators: [Validators.required, Validators.min(0)] })
        });
    }

    private updateAssignedCriteriaFormArrays(type: DiscountFormData['assignmentType'], sourceCriteria?: AssignedCriteria): void {
        if (!this.assignedCriteriaControl) return;

        this.clearAssignedCriteriaFormArrays();

        switch (type) {
            case 'GLOBAL':        this.populateCriteriaFormArray(this.busIdsFormArray, sourceCriteria?.busIds); break;
            case 'PARTNER':       this.populateCriteriaFormArray(this.partnerIdsFormArray, sourceCriteria?.partnerIds); break;
            case 'ENTITY':        this.populateCriteriaFormArray(this.entityIdsFormArray, sourceCriteria?.entityIds); break;
            case 'ROUTE':         this.populateCriteriaFormArray(this.routeIdsFormArray, sourceCriteria?.routeIds); break;
            case 'BUS':           this.populateCriteriaFormArray(this.busIdsFormArray, sourceCriteria?.busIds); break;
            case 'PARTNER_ENTITY': this.populateMapFormArray(this.partnerEntityMapGroupsControl, sourceCriteria?.partnerEntityMap); break;
            case 'ROUTE_ENTITY':  this.populateMapFormArray(this.routeEntityMapGroupsControl, sourceCriteria?.routeEntityMap); break;
            case 'PARTNER_ROUTE': this.populateMapFormArray(this.partnerRouteMapGroupsControl, sourceCriteria?.partnerRouteMap); break;
            default: break;
        }
    }

    private populateCriteriaFormArray(formArray: FormArray<FormGroup<CriteriaEntryFormGroup>> | undefined, entries?: CriteriaEntry[]): void {
        if (!formArray || !entries) return;

        entries.forEach(entry => formArray.push(this.createCriteriaEntryFormGroup(entry)));
    }

    private populateMapFormArray(formArray: FormArray<FormGroup<MapEntryFormGroup>> | undefined, mapObj?: { [key: string]: CriteriaEntry[] }): void {
        if (!formArray || !mapObj) return;

        Object.entries(mapObj).forEach(([key, entries]) => {
            formArray.push(this.fb.group<MapEntryFormGroup>({
                key                : this.fb.control(key, { nonNullable: true, validators: [Validators.required] }),
                values             : this.fb.array<FormGroup<CriteriaEntryFormGroup>>(entries.map(e => this.createCriteriaEntryFormGroup(e))),
                valueSearchControl : this.fb.control(null)
            }));
        });
    }

    private clearAssignedCriteriaFormArrays(): void {
        if (!this.assignedCriteriaControl) return;

        [this.partnerIdsFormArray, this.entityIdsFormArray, this.routeIdsFormArray, this.busIdsFormArray].forEach(a => a?.clear());
        [this.partnerEntityMapGroupsControl, this.routeEntityMapGroupsControl, this.partnerRouteMapGroupsControl].forEach(a => a?.clear());

        this.partnerSearchControl.setValue(null, { emitEvent: false });
        this.entitySearchControl.setValue(null, { emitEvent: false });
        this.routeSearchControl.setValue(null, { emitEvent: false });
        this.busSearchControl.setValue(null, { emitEvent: false });
        this.mapPartnerKeySearchControl.setValue(null, { emitEvent: false });
        this.mapRouteKeySearchControl.setValue(null, { emitEvent: false });
    }

    // ─── Filtered buses per entry ────────────────────────────────────────────────

    getFilteredBusesForEntry(entryGroup: FormGroup<CriteriaEntryFormGroup>, contextType: 'partner' | 'entity' | 'route'): Observable<Bus[]> {
        return combineLatest([
            entryGroup.controls.busSearchControl.valueChanges.pipe(startWith('')),
            this._busesSubject.asObservable()
        ]).pipe(
            map(([value]) => {
                const elementId      = entryGroup.controls.id.value;
                const selectedBusIds = entryGroup.controls.buses.value.map((b: any) => b.id);
                let allowedBuses: Bus[] = [];

                switch (contextType) {
                    case 'partner': allowedBuses = this.buses.filter(b => b.partnerId === elementId); break;
                    case 'entity':  allowedBuses = this.buses.filter(b => b.entityId === elementId); break;
                    case 'route':   allowedBuses = this.buses.filter(b => b.routeId === elementId); break;
                }

                return this._filterItems(value, allowedBuses, selectedBusIds, 'name', 'id');
            })
        );
    }

    getFilteredBusesForMapEntry(entryGroup: FormGroup<CriteriaEntryFormGroup>, parentId: string, contextType: 'partner_entity' | 'route_entity' | 'partner_route'): Observable<Bus[]> {
        return combineLatest([
            entryGroup.controls.busSearchControl.valueChanges.pipe(startWith('')),
            this._busesSubject.asObservable()
        ]).pipe(
            map(([value]) => {
                const elementId      = entryGroup.controls.id.value;
                const selectedBusIds = entryGroup.controls.buses.value.map((b: any) => b.id);
                let allowedBuses: Bus[] = [];

                switch (contextType) {
                    case 'partner_entity': allowedBuses = this.buses.filter(b => b.partnerId === parentId && b.entityId === elementId); break;
                    case 'route_entity':   allowedBuses = this.buses.filter(b => b.routeId === parentId && b.entityId === elementId); break;
                    case 'partner_route':  allowedBuses = this.buses.filter(b => b.partnerId === parentId && b.routeId === elementId); break;
                }

                return this._filterItems(value, allowedBuses, selectedBusIds, 'name', 'id');
            })
        );
    }

    // ─── Add / remove buses within entries ───────────────────────────────────────

    addBusToEntry(event: MatAutocompleteSelectedEvent, entryGroup: FormGroup<CriteriaEntryFormGroup>): void {
        const bus       = event.option.value as Bus;
        const parentAmt = entryGroup.controls.amount.value;
        const exists    = entryGroup.controls.buses.controls.some(g => g.controls.id.value === bus.id);

        if (!exists) {
            entryGroup.controls.buses.push(this.createBusEntryFormGroup({ id: bus.id }, parentAmt));
            entryGroup.controls.buses.markAsDirty();
        }

        setTimeout(() => {
            entryGroup.controls.busSearchControl.setValue(null);
            this.cdr.detectChanges();
        }, 0);
    }

    removeBusFromEntry(entryGroup: FormGroup<CriteriaEntryFormGroup>, index: number): void {
        entryGroup.controls.buses.removeAt(index);
        entryGroup.controls.buses.markAsDirty();
        this.cdr.detectChanges();
    }

    // ─── Add / remove main elements ──────────────────────────────────────────────

    selectedPartner(event: MatAutocompleteSelectedEvent): void { this._addEntry(event, this.partnerIdsFormArray, 'uid', this.partnerSearchControl, this.partnerInputRef); }
    selectedEntity(event: MatAutocompleteSelectedEvent): void  { this._addEntry(event, this.entityIdsFormArray, 'id', this.entitySearchControl, this.entityInputRef); }
    selectedRoute(event: MatAutocompleteSelectedEvent): void   { this._addEntry(event, this.routeIdsFormArray, 'id', this.routeSearchControl, this.routeInputRef); }
    selectedBus(event: MatAutocompleteSelectedEvent): void     { this._addEntry(event, this.busIdsFormArray, 'id', this.busSearchControl, this.busInputRef); }

    private _addEntry(event: MatAutocompleteSelectedEvent, formArray: FormArray<FormGroup<CriteriaEntryFormGroup>> | undefined, idKey: string, searchControl: FormControl<string | null>, inputRef: ElementRef<HTMLInputElement>): void {
        if (!formArray) return;

        const selectedItem = event.option.value;
        const idToAdd      = (selectedItem as any)[idKey];

        if (!formArray.controls.some(g => g.controls.id.value === idToAdd)) {
            formArray.push(this.createCriteriaEntryFormGroup({ id: idToAdd }));
            formArray.markAsDirty();
        }

        setTimeout(() => {
            searchControl.setValue(null);
            inputRef.nativeElement.value = '';
            inputRef.nativeElement.focus();
            this.cdr.detectChanges();
        }, 0);
    }

    removePartner(id: string): void { this._removeEntry(id, this.partnerIdsFormArray, this.partnerSearchControl, this.partnerInputRef); }
    removeEntity(id: string): void  { this._removeEntry(id, this.entityIdsFormArray, this.entitySearchControl, this.entityInputRef); }
    removeRoute(id: string): void   { this._removeEntry(id, this.routeIdsFormArray, this.routeSearchControl, this.routeInputRef); }
    removeBus(id: string): void     { this._removeEntry(id, this.busIdsFormArray, this.busSearchControl, this.busInputRef); }

    private _removeEntry(id: string, formArray: FormArray<FormGroup<CriteriaEntryFormGroup>> | undefined, searchControl: FormControl<string | null>, inputRef: ElementRef<HTMLInputElement>): void {
        if (!formArray) return;

        const index = formArray.controls.findIndex(g => g.controls.id.value === id);

        if (index >= 0) {
            formArray.removeAt(index);
            formArray.markAsDirty();

            setTimeout(() => {
                searchControl.setValue(null);
                inputRef.nativeElement.value = '';
                this.cdr.detectChanges();
            }, 0);
        }
    }

    // ─── Map key add / remove ────────────────────────────────────────────────────

    addMapKeyToPartnerEntity(event: MatAutocompleteSelectedEvent): void { this._addMapKey(event, this.partnerEntityMapGroupsControl, 'uid', this.mapPartnerKeySearchControl, this.mapPartnerKeyInputRef); }
    addMapKeyToRouteEntity(event: MatAutocompleteSelectedEvent): void   { this._addMapKey(event, this.routeEntityMapGroupsControl, 'id', this.mapRouteKeySearchControl, this.mapRouteKeyInputRef); }
    addMapKeyToPartnerRoute(event: MatAutocompleteSelectedEvent): void  { this._addMapKey(event, this.partnerRouteMapGroupsControl, 'uid', this.mapPartnerKeySearchControl, this.mapPartnerKeyInputRef); }

    private _addMapKey(event: MatAutocompleteSelectedEvent, formArray: FormArray<FormGroup<MapEntryFormGroup>> | undefined, idKey: string, searchControl: FormControl<Partner | Route | string | null>, inputRef: ElementRef<HTMLInputElement>): void {
        if (!formArray) return;

        const selectedItem = event.option.value;
        const keyToAdd     = (selectedItem as any)[idKey];

        if (!formArray.controls.some(c => c.controls.key.value === keyToAdd)) {
            formArray.push(this.fb.group<MapEntryFormGroup>({
                key                : this.fb.control(keyToAdd, { nonNullable: true, validators: [Validators.required] }),
                values             : this.fb.array<FormGroup<CriteriaEntryFormGroup>>([]),
                valueSearchControl : this.fb.control(null)
            }));

            formArray.markAsDirty();
        }

        setTimeout(() => {
            searchControl.setValue(null);
            inputRef.nativeElement.value = '';
            inputRef.nativeElement.focus();
            this.cdr.detectChanges();
        }, 0);
    }

    removeMapKeyEntry(index: number, formArray: FormArray | undefined): void {
        if (!formArray) return;

        formArray.removeAt(index);
        formArray.markAsDirty();
        this.cdr.detectChanges();
    }

    selectValueForMapEntry(event: MatAutocompleteSelectedEvent, groupControl: FormGroup<MapEntryFormGroup>): void {
        const selectedItem  = event.option.value;
        const valueIdToAdd  = (selectedItem as any).id;
        const valuesArray   = groupControl.controls.values;

        if (!valuesArray.controls.some(g => g.controls.id.value === valueIdToAdd)) {
            valuesArray.push(this.createCriteriaEntryFormGroup({ id: valueIdToAdd }));
            valuesArray.markAsDirty();
        }

        setTimeout(() => {
            groupControl.controls.valueSearchControl.setValue(null);
            this.cdr.detectChanges();
        }, 0);
    }

    removeValueFromMapEntry(valueId: string, groupControl: FormGroup<MapEntryFormGroup>): void {
        const valuesArray = groupControl.controls.values;
        const index       = valuesArray.controls.findIndex(g => g.controls.id.value === valueId);

        if (index >= 0) {
            valuesArray.removeAt(index);
            valuesArray.markAsDirty();
            this.cdr.detectChanges();
        }
    }

    // ─── Filtered observables for map entries ────────────────────────────────────

    filteredMapPartnerEntities$ = (groupControl: FormGroup<MapEntryFormGroup>): Observable<Entity[]> =>
        combineLatest([
            groupControl.controls.valueSearchControl.valueChanges.pipe(startWith('')),
            this._entitiesSubject.asObservable()
        ]).pipe(
            map(([value]) => {
                const partnerId       = groupControl.controls.key.value;
                const allowedEntities = this.getEntitiesForPartners(partnerId);
                return this._filterItems(value, allowedEntities, groupControl.controls.values.value?.map((e: any) => e.id) || [], 'name', 'id');
            })
        );

    filteredMapRouteEntities$ = (groupControl: FormGroup<MapEntryFormGroup>): Observable<Entity[]> =>
        combineLatest([
            groupControl.controls.valueSearchControl.valueChanges.pipe(startWith('')),
            this._entitiesSubject.asObservable()
        ]).pipe(
            map(([value]) => {
                const routeId         = groupControl.controls.key.value;
                const allowedEntities = this.getEntitiesForRoutes(routeId);
                return this._filterItems(value, allowedEntities, groupControl.controls.values.value?.map((e: any) => e.id) || [], 'name', 'id');
            })
        );

    filteredMapPartnerRoutes$ = (groupControl: FormGroup<MapEntryFormGroup>): Observable<Route[]> =>
        combineLatest([
            groupControl.controls.valueSearchControl.valueChanges.pipe(startWith('')),
            this._routesSubject.asObservable()
        ]).pipe(
            map(([value]) => {
                const partnerId     = groupControl.controls.key.value;
                const allowedRoutes = this.getRoutesForPartners(partnerId);
                return this._filterItems(value, allowedRoutes, groupControl.controls.values.value?.map((r: any) => r.id) || [], 'name', 'id');
            })
        );

    // ─── Display helpers ─────────────────────────────────────────────────────────

    displayPartnerName(partner: Partner | string | null): string {
        if (!partner) return '';
        if (typeof partner === 'string') return this.partners.find(p => p.uid === partner)?.displayName || partner;
        return partner.displayName || '';
    }

    displayEntityName(entity: Entity | string | null): string {
        if (!entity) return '';
        if (typeof entity === 'string') return this.entities.find(e => e.id === entity)?.name || entity;
        return entity.name || '';
    }

    displayRouteName(route: Route | string | null): string {
        if (!route) return '';
        if (typeof route === 'string') return this.routes.find(r => r.id === route)?.name || route;
        return route.name || '';
    }

    displayBusName(bus: Bus | string | null): string {
        if (!bus) return '';
        if (typeof bus === 'string') {
            const found = this.buses.find(b => b.id === bus);
            return found ? `${found.name} (${found.licensePlate || 'N/A'})` : bus;
        }
        return `${bus.name || ''} (${bus.licensePlate || 'N/A'})`;
    }

    getChipDisplayForPartner(id: string): string { return this.partners.find(p => p.uid === id)?.displayName || this.partners.find(p => p.uid === id)?.email || id; }
    getChipDisplayForEntity(id: string): string  { return this.entities.find(e => e.id === id)?.name || id; }
    getChipDisplayForRoute(id: string): string   { return this.routes.find(r => r.id === id)?.name || id; }
    getChipDisplayForBus(id: string): string {
        const bus = this.buses.find(b => b.id === id);
        return bus?.name ? `${bus.name} (${bus.licensePlate || 'N/A'})` : id;
    }

    // ─── Relation helpers ────────────────────────────────────────────────────────

    getEntitiesForPartners(partnerId: string): Entity[] {
        if (!partnerId || !this.buses || !this.entities) return [];
        const ids = new Set<string>();
        this.buses.filter(b => b.partnerId === partnerId).forEach(b => ids.add(b.entityId));
        return this.entities.filter(e => ids.has(e.id));
    }

    getEntitiesForRoutes(routeId: string): Entity[] {
        if (!routeId || !this.buses || !this.entities) return [];
        const ids = new Set<string>();
        this.buses.filter(b => b.routeId === routeId).forEach(b => ids.add(b.entityId));
        return this.entities.filter(e => ids.has(e.id));
    }

    getRoutesForPartners(partnerId: string): Route[] {
        if (!partnerId || !this.buses || !this.routes) return [];
        const ids = new Set<string>();
        this.buses.filter(b => b.partnerId === partnerId).forEach(b => ids.add(b.routeId));
        return this.routes.filter(r => ids.has(r.id));
    }

    // ─── Filter helper ───────────────────────────────────────────────────────────

    private _filterItems(value: string | object | null, allItems: any[], selectedIds: string[], displayKey: string, idKey: string): any[] {
        let filterValue = '';

        if (typeof value === 'string') {
            filterValue = value.toLowerCase();
        } else if (value && typeof value === 'object' && (value as any)[displayKey]) {
            filterValue = (value as any)[displayKey].toLowerCase();
        }

        return allItems.filter(item =>
            !selectedIds.includes(item[idKey]) &&
            (filterValue === '' || String((item as any)[displayKey]).toLowerCase().includes(filterValue))
        );
    }

    // ─── Load data ───────────────────────────────────────────────────────────────

    async loadRelatedData(): Promise<void> {
        const [entitiesResult, routesResult, partnersResult, busesResult] = await Promise.all([
            this.adminService.getAllEntities(),
            this.adminService.getAllRoutes(),
            this.adminService.getAllPartners(),
            this.adminService.getAllBuses(),
        ]);

        this.entities = entitiesResult || [];
        this.routes   = routesResult   || [];
        this.partners = partnersResult || [];
        this.buses    = busesResult    || [];
    }

    // ─── Submit ──────────────────────────────────────────────────────────────────

    onSubmit(): void {
        this.discountForm.markAllAsTouched();

        if (this.discountForm.valid) {
            const formData       = this.discountForm.getRawValue();
            const assignmentType = formData.assignmentType as DiscountFormData['assignmentType'];

            const resultData: DiscountFormData = {
                id                  : this.data?.id,
                name                : formData.name,
                defaultAmount       : Number(formData.defaultAmount),
                calculationStrategy : formData.calculationStrategy,
                assignmentType,
                status              : !!formData.status
            };

            if (this.assignedCriteriaControl) {
                const criteria = this.buildAssignedCriteriaPayload();
                if (criteria) {
                    if (assignmentType === 'GLOBAL') {
                        if (criteria.busIds && criteria.busIds.length > 0) {
                            resultData.assignedCriteria = { busIds: criteria.busIds };
                        }
                    } else {
                        resultData.assignedCriteria = criteria;
                    }
                }
            }

            this.dialogRef.close(resultData);
        }
    }

    private buildAssignedCriteriaPayload(): AssignedCriteria | undefined {
        if (!this.assignedCriteriaControl) return undefined;

        const rawValue = this.assignedCriteriaControl.getRawValue();
        const cleaned: AssignedCriteria = {};

        const normalizeEntries = (entries: any[]): CriteriaEntry[] | undefined => {
            if (!entries || entries.length === 0) return undefined;

            return entries.map(entry => {
                const result: CriteriaEntry = { id: entry.id, amount: Number(entry.amount) };

                if (entry.buses && entry.buses.length > 0) {
                    result.buses = entry.buses.map((b: any): BusEntry => ({ id: b.id, amount: Number(b.amount) }));
                }

                return result;
            });
        };

        const normalizeMapEntries = (entries: any[]): { [key: string]: CriteriaEntry[] } | undefined => {
            if (!entries || entries.length === 0) return undefined;

            const result: { [key: string]: CriteriaEntry[] } = {};

            entries.forEach(entry => {
                const normalized = normalizeEntries(entry.values);
                if (normalized && normalized.length > 0) result[entry.key] = normalized;
            });

            return Object.keys(result).length > 0 ? result : undefined;
        };

        const partnerEntries = normalizeEntries(rawValue.partnerIds);
        if (partnerEntries) cleaned.partnerIds = partnerEntries;

        const entityEntries = normalizeEntries(rawValue.entityIds);
        if (entityEntries) cleaned.entityIds = entityEntries;

        const routeEntries = normalizeEntries(rawValue.routeIds);
        if (routeEntries) cleaned.routeIds = routeEntries;

        const busEntries = normalizeEntries(rawValue.busIds);
        if (busEntries) cleaned.busIds = busEntries;

        const partnerEntityMap = normalizeMapEntries(rawValue.partnerEntityMap);
        if (partnerEntityMap) cleaned.partnerEntityMap = partnerEntityMap;

        const routeEntityMap = normalizeMapEntries(rawValue.routeEntityMap);
        if (routeEntityMap) cleaned.routeEntityMap = routeEntityMap;

        const partnerRouteMap = normalizeMapEntries(rawValue.partnerRouteMap);
        if (partnerRouteMap) cleaned.partnerRouteMap = partnerRouteMap;

        return Object.keys(cleaned).length > 0 ? cleaned : undefined;
    }

    updateEntryAmount(group: FormGroup<CriteriaEntryFormGroup>, rawValue: string): void {
        const num = parseFloat(rawValue);
        group.controls.amount.setValue(isNaN(num) ? null : num);
        group.controls.amount.markAsDirty();
    }

    onCancel(): void {
        this.dialogRef.close(undefined);
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
